import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Film, Download, Copy, Play, Pause, Loader2, RefreshCw, Sparkles, Music2, AlertCircle,
  ArrowUp, ArrowDown, EyeOff, Upload, GripVertical, Video, Mic, FileText, Globe, Lock, ExternalLink, Crown, Clapperboard, ArrowRight } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { UpgradeDialog, ExpiryBanner } from "@/components/Upgrade";
import api, { fileUrl } from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { getTheme } from "@/lib/eventTheme";

const KIND_ICON = { video: Video, audio: Mic, text: FileText };

function TributeToStoryCard({ eventId, honoreeName }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/events/${eventId}/to-story`);
      toast.success(r.data.already ? "Opening the story studio…" : `Imported ${r.data.imported_clips} clips & ${r.data.imported_photos} photos`);
      navigate(`/stories/${r.data.story_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the story"); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-[hsl(var(--film-border))] bg-[hsl(var(--letterbox))] text-[hsl(var(--letterbox-foreground))] overflow-hidden" data-testid="tribute-to-story-card">
      <div className="p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-white/10 shrink-0"><Clapperboard size={20} /></div>
          <div className="min-w-0">
            <h4 className="font-display text-lg font-semibold">Turn these memories into {honoreeName ? `${honoreeName}'s` : "their"} life story</h4>
            <p className="text-sm text-white/70">Import every tribute and photo into a full Keepsake Story documentary — add interviews anytime.</p>
          </div>
        </div>
        <button data-testid="tribute-to-story-button" onClick={go} disabled={busy}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 shrink-0">
          {busy ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />} Create the story
        </button>
      </div>
    </div>
  );
}

export default function CompilationPanel({ ev, onRefresh }) {
  const eventId = ev.event_id;
  const theme = getTheme(ev.type);
  const [comp, setComp] = useState(ev.compilation || { status: "none", progress: 0 });
  const [meta, setMeta] = useState({ compiles_used: ev.compile_count || 0, compiles_limit: 2, entitlements: ev.entitlements || {}, films: ev.films || [] });
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [track, setTrack] = useState(ev.compilation?.track_id || null);
  const [customMusic, setCustomMusic] = useState(null);
  const [thankyou, setThankyou] = useState(ev.compilation?.thankyou || "");
  const [clips, setClips] = useState([]);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [introTitle, setIntroTitle] = useState(ev.compilation?.intro_title || "");
  const [highlight, setHighlight] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [watchPublic, setWatchPublic] = useState(ev.watch_public !== false);
  const [watchBusy, setWatchBusy] = useState(false);
  const audioRef = useRef(null);
  const musicFileRef = useRef(null);
  const pollRef = useRef(null);

  const loadClips = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}/submissions`);
      let approved = res.data.filter((s) => s.status === "approved");
      const order = ev.clip_order || [];
      if (order.length) {
        const idx = {}; order.forEach((id, i) => (idx[id] = i));
        approved.sort((a, b) => (idx[a.submission_id] ?? 999) - (idx[b.submission_id] ?? 999));
      }
      setClips(approved);
    } catch {}
  }, [eventId, ev.clip_order]);

  const poll = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}/compilation`);
      setComp(res.data.compilation);
      setMeta({ compiles_used: res.data.compiles_used, compiles_limit: res.data.compiles_limit, entitlements: res.data.entitlements || {}, films: res.data.films || [] });
      if (res.data.compilation.status !== "processing") {
        clearInterval(pollRef.current); pollRef.current = null;
        onRefresh && onRefresh();
        if (res.data.compilation.status === "ready") toast.success("Your tribute film is ready");
        if (res.data.compilation.status === "failed") toast.error(res.data.compilation.error || "Compilation failed");
      }
    } catch {}
  }, [eventId, onRefresh]);

  useEffect(() => {
    api.get(`/events/${eventId}/music`).then((r) => { setTracks(r.data.tracks); if (!track) setTrack(r.data.tracks[0]?.id); }).catch(() => {});
    loadClips();
    if ((ev.compilation?.status) === "processing") startPolling();
    return () => { clearInterval(pollRef.current); if (audioRef.current) audioRef.current.pause(); };
    // eslint-disable-next-line
  }, []);

  const startPolling = () => { clearInterval(pollRef.current); pollRef.current = setInterval(poll, 3000); };

  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= clips.length) return;
    const next = [...clips]; [next[i], next[j]] = [next[j], next[i]]; setClips(next);
  };

  // native drag-and-drop reorder
  const onDragStart = (i) => setDragIndex(i);
  const onDragEnter = (i) => { if (dragIndex !== null) setOverIndex(i); };
  const onDrop = (i) => {
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); setOverIndex(null); return; }
    setClips((cl) => {
      const next = [...cl];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(null); setOverIndex(null);
  };
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const toggleWatchPublic = async (val) => {
    setWatchPublic(val); setWatchBusy(true);
    try {
      await api.patch(`/events/${eventId}`, { watch_public: val });
      toast.success(val ? "Watch page is now public" : "Watch page is now private");
      onRefresh && onRefresh();
    } catch {
      setWatchPublic(!val);
      toast.error("Could not update watch page privacy");
    } finally { setWatchBusy(false); }
  };
  const hideClip = async (c) => {
    try { await api.patch(`/events/${eventId}/submissions/${c.submission_id}`, { status: "hidden" });
      setClips((cl) => cl.filter((x) => x.submission_id !== c.submission_id)); toast.success("Hidden from film"); onRefresh && onRefresh(); }
    catch { toast.error("Could not hide"); }
  };

  const compile = async () => {
    setBusy(true);
    try {
      const order = clips.map((c) => c.submission_id);
      await api.post(`/events/${eventId}/arrange`, { order });
      await api.post(`/events/${eventId}/compile`, { track_id: customMusic ? "none" : track, custom_music_file_id: customMusic?.file_id || null, thankyou: thankyou || null, order, intro_title: introTitle || null, highlight: !!highlight, version_label: highlight ? "Highlight reel" : "Full film" });
      setComp({ status: "processing", progress: 3, stage: "Gathering tributes" });
      startPolling();
      toast.message("Weaving your tribute film…", { description: "You can leave this page; we'll email you when it's ready." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start compilation");
    } finally { setBusy(false); }
  };

  const uploadMusic = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio")) { toast.error("Please choose an audio file"); return; }
    try { toast.message("Uploading soundtrack…"); const up = await chunkedUpload(file, file.name, file.type, () => {}); setCustomMusic({ file_id: up.file_id, name: file.name }); toast.success("Soundtrack added"); }
    catch { toast.error("Could not upload soundtrack"); }
  };

  const previewTrack = (id) => {
    if (id === "none") return;
    if (!audioRef.current) audioRef.current = new Audio();
    if (playing === id) { audioRef.current.pause(); setPlaying(null); return; }
    audioRef.current.src = `${api.defaults.baseURL}/music/${id}`;
    audioRef.current.play().then(() => setPlaying(id)).catch(() => {});
    audioRef.current.onended = () => setPlaying(null);
  };

  const streamUrl = comp.file_id ? fileUrl(comp.file_id) : "";
  const processing = comp.status === "processing";
  const canCompile = meta.compiles_limit == null || (meta.compiles_used || 0) < meta.compiles_limit;

  if (processing) {
    const eta = comp.eta_seconds;
    const etaTxt = eta == null ? "" : eta > 90 ? `~${Math.ceil(eta / 60)} minutes remaining` : `~${Math.max(5, eta)} seconds remaining`;
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 text-center">
        <Loader2 className="mx-auto animate-spin" style={{ color: `hsl(${theme.accent})` }} size={30} />
        <h3 className="mt-4 font-display text-xl font-semibold">{comp.stage || "Weaving your tribute film…"}</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1" data-testid="compile-eta">
          {comp.clips_total ? `Preparing ${comp.clips_total} tributes… ` : ""}{etaTxt || "This can take a minute or two."}
        </p>
        <div className="mt-5 max-w-sm mx-auto h-3 rounded-full bg-[hsl(var(--muted))] overflow-hidden"><div className="h-full transition-all" style={{ width: `${comp.progress || 0}%`, background: `hsl(${theme.accent})` }} /></div>
        <p className="mt-2 text-sm font-semibold tabular-nums">{comp.progress || 0}%</p>
      </div>
    );
  }

  if (comp.status === "ready") {
    const paid = meta.entitlements?.paid;
    return (
      <div className="space-y-5">
        {!paid && <ExpiryBanner deleteAt={comp.delete_at} onUpgrade={() => setUpgradeOpen(true)} />}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
          <video data-testid="tribute-film-video" src={streamUrl} controls playsInline className="w-full bg-black aspect-video" />
          <div className="p-5">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: `hsl(${theme.accent})` }}><Sparkles size={16} /> Your tribute film is ready</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              {comp.clip_count} tributes · {(comp.size / 1048576).toFixed(1)} MB · {paid ? "1080p · no watermark · shareable link" : "with GatherVoices watermark · upgrade to remove"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <a data-testid="download-film-button" href={streamUrl} download={`gathervoices-${ev.slug}.mp4`} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold" style={{ background: `hsl(${theme.accent})` }}><Download size={16} /> Download MP4</a>
              <button data-testid="copy-stream-link-button" onClick={() => { navigator.clipboard.writeText(streamUrl); toast.success("Streamable link copied"); }} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]"><Copy size={16} /> Copy link</button>
              {canCompile ? (
                <button data-testid="recompile-button" onClick={() => setComp({ status: "none", progress: 0 })} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]"><RefreshCw size={16} /> Make a new version</button>
              ) : (
                <button data-testid="recompile-upgrade-button" onClick={() => setUpgradeOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]"><Crown size={16} /> Upgrade for unlimited versions</button>
              )}
              {!paid && (
                <button data-testid="keep-forever-button" onClick={() => setUpgradeOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity"><Crown size={16} /> Keep forever · $29</button>
              )}
            </div>
          </div>
        </div>
        <WatchShareCard slug={ev.slug} theme={theme} watchPublic={watchPublic} onToggle={toggleWatchPublic} busy={watchBusy} />
        {ev.type === "memorial" && <TributeToStoryCard eventId={eventId} honoreeName={ev.honoree_name} />}
        <VersionsList versions={meta.films || ev.films} theme={theme} slug={ev.slug} />
        <RawDownloads clips={clips} theme={theme} />
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} eventId={eventId}
          reason="Your film is ready — keep it forever with no watermark, and unlock a private streaming page for family." />
      </div>
    );
  }

  // setup view
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}><Film size={22} /></div>
          <div>
            <h3 className="font-display text-xl font-semibold">Create the tribute film</h3>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">One polished 1080p film — title cards, name captions, crossfades & music.</p>
          </div>
        </div>
        {comp.status === "failed" && <div className="mt-4 flex items-start gap-2 rounded-xl bg-[hsl(0_72%_96%)] text-[hsl(var(--destructive))] p-3 text-sm"><AlertCircle size={16} className="mt-0.5" /> {comp.error}</div>}

        {/* Arrange */}
        <div className="mt-6">
          <label className="text-sm font-medium">Arrange your video ({clips.length})</label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Drag the handle to reorder, use the arrows, or hide any from the film.</p>
          {clips.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No approved tributes yet — approve some in the Tributes tab.</p>
          ) : (
            <div className="space-y-2" data-testid="arrange-list">
              {clips.map((c, i) => {
                const Icon = KIND_ICON[c.kind] || FileText;
                const dragging = dragIndex === i;
                const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
                return (
                  <div key={c.submission_id} data-testid={`arrange-row-${c.submission_id}`}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragEnter={() => onDragEnter(i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(i)}
                    onDragEnd={onDragEnd}
                    className={`flex items-center gap-3 rounded-xl border bg-[hsl(var(--background))] p-2.5 transition-[opacity,box-shadow] duration-150 ${dragging ? "opacity-50" : "opacity-100"}`}
                    style={{ borderColor: isOver ? `hsl(${theme.accent})` : "hsl(var(--border))", boxShadow: isOver ? `0 0 0 2px hsl(${theme.accent} / 0.35)` : "none" }}>
                    <span data-testid={`drag-handle-${c.submission_id}`} className="cursor-grab active:cursor-grabbing text-[hsl(var(--muted-foreground))] touch-none" title="Drag to reorder"><GripVertical size={16} /></span>
                    <span className="h-8 w-8 grid place-items-center rounded-lg shrink-0" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}><Icon size={15} /></span>
                    <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{c.contributor_name}</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.relationship || c.kind}</div></div>
                    <button data-testid={`move-up-${c.submission_id}`} onClick={() => move(i, -1)} disabled={i === 0} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] disabled:opacity-30"><ArrowUp size={15} /></button>
                    <button data-testid={`move-down-${c.submission_id}`} onClick={() => move(i, 1)} disabled={i === clips.length - 1} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] disabled:opacity-30"><ArrowDown size={15} /></button>
                    <button data-testid={`hide-${c.submission_id}`} onClick={() => hideClip(c)} className="h-8 w-8 grid place-items-center rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"><EyeOff size={15} /></button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Music */}
        <div className="mt-6">
          <label className="text-sm font-medium flex items-center gap-1.5"><Music2 size={15} /> Background music</label>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {tracks.map((t) => {
              const active = !customMusic && track === t.id;
              return (
                <button key={t.id} data-testid={`music-${t.id}`} onClick={() => { setTrack(t.id); setCustomMusic(null); }}
                  className="flex items-center justify-between gap-2 rounded-xl border-2 p-3 text-left bg-[hsl(var(--background))]" style={{ borderColor: active ? `hsl(${theme.accent})` : "hsl(var(--border))" }}>
                  <div><div className="font-semibold text-sm">{t.name}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{t.mood}</div></div>
                  {t.id !== "none" && <span onClick={(e) => { e.stopPropagation(); previewTrack(t.id); }} className="h-8 w-8 grid place-items-center rounded-full shrink-0" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>{playing === t.id ? <Pause size={15} /> : <Play size={15} />}</span>}
                </button>
              );
            })}
            <button data-testid="music-upload" onClick={() => musicFileRef.current?.click()} className="flex items-center justify-between gap-2 rounded-xl border-2 p-3 text-left bg-[hsl(var(--background))]" style={{ borderColor: customMusic ? `hsl(${theme.accent})` : "hsl(var(--border))" }}>
              <div><div className="font-semibold text-sm flex items-center gap-1.5"><Upload size={14} /> Your own soundtrack</div><div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{customMusic ? customMusic.name : "Upload an audio file"}</div></div>
            </button>
            <input ref={musicFileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => uploadMusic(e.target.files?.[0])} />
          </div>
        </div>

        {/* Thank-you */}
        <div className="mt-6">
          <label className="text-sm font-medium">Closing thank-you message (optional)</label>
          <textarea data-testid="thankyou-input" value={thankyou} onChange={(e) => setThankyou(e.target.value)} rows={2} placeholder="A short note shown on the closing card…" className="mt-1.5 w-full rounded-xl p-3 text-sm bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none" />
        </div>

        {(meta.entitlements?.tier === "legacy" || meta.entitlements?.tier === "pro") && (
          <div className="mt-6 rounded-2xl border p-5" style={{ borderColor: `hsl(${theme.accent})`, background: `hsl(${theme.accentSoft})` }} data-testid="legacy-options">
            <div className="flex items-center gap-2 font-display text-lg font-semibold" style={{ color: `hsl(${theme.accentInk})` }}><Crown size={18} /> Legacy touches</div>
            <div className="mt-3">
              <label className="text-sm font-medium">Custom intro title</label>
              <input data-testid="intro-title-input" value={introTitle} onChange={(e) => setIntroTitle(e.target.value)} placeholder="e.g. A Celebration of a Life Well Lived"
                className="mt-1.5 w-full rounded-xl p-3 text-sm bg-[hsl(var(--card))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
            </div>
            <label className="mt-3 flex items-start gap-3 cursor-pointer">
              <Switch data-testid="highlight-toggle" checked={highlight} onCheckedChange={setHighlight} />
              <span className="text-sm">Auto-cut a <strong>3–5 minute highlight reel</strong> — we pick the most heartfelt tributes and trim each to its best moments, saved as its own version</span>
            </label>
          </div>
        )}

        <button data-testid="compile-button" disabled={busy || clips.length === 0 || !canCompile} onClick={compile} className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-semibold disabled:opacity-50" style={{ background: `hsl(${theme.accent})` }}>
          <Sparkles size={18} /> {busy ? "Starting…" : "Create tribute film"}
        </button>
        {!canCompile && (
          <button data-testid="setup-upgrade-button" onClick={() => setUpgradeOpen(true)} className="mt-6 ml-2 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
            <Crown size={18} /> Upgrade to Keepsake
          </button>
        )}
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          {!canCompile ? "You've used your free films. Upgrade to Keepsake for unlimited recompiles." :
            meta.compiles_limit == null ? "Unlimited recompiles included." :
            `${clips.length} tribute${clips.length === 1 ? "" : "s"} will be included · ${meta.compiles_used || 0}/${meta.compiles_limit} compiles used (free tier).`}
        </p>
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} eventId={eventId}
          reason="Upgrade to Keepsake for unlimited tributes, longer clips, no watermark, and unlimited recompiles." />
      </div>
    </div>
  );
}

function VersionsList({ versions, theme, slug }) {
  if (!versions || versions.length < 2) return null;
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="versions-list">
      <h4 className="font-display text-lg font-semibold flex items-center gap-2"><Film size={18} style={{ color: `hsl(${theme.accent})` }} /> Saved versions</h4>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">Every film you compile is kept here (Legacy).</p>
      <div className="mt-3 divide-y divide-[hsl(var(--border))]">
        {[...versions].reverse().map((v) => (
          <div key={v.version_id} data-testid={`version-${v.version_id}`} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <div className="font-medium truncate">{v.label}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{v.clip_count} tributes · {(v.size / 1048576).toFixed(1)} MB</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <a href={fileUrl(v.file_id)} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-semibold hover:bg-[hsl(var(--muted))] inline-flex items-center gap-1.5"><Play size={14} /> Watch</a>
              <a href={fileUrl(v.file_id)} download={`gathervoices-${slug}-${v.label.replace(/\s+/g, "-").toLowerCase()}.mp4`} className="px-3 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-1.5" style={{ background: `hsl(${theme.accent})` }}><Download size={14} /> Save</a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WatchShareCard({ slug, theme, watchPublic, onToggle, busy }) {
  const watchUrl = `${window.location.origin}/watch/${slug}`;
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="watch-share-card">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 grid place-items-center rounded-xl" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>
          {watchPublic ? <Globe size={20} /> : <Lock size={20} />}
        </div>
        <div className="flex-1">
          <h4 className="font-display text-lg font-semibold">Family watch page</h4>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">A beautiful page for relatives who can't attend — no login needed.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">{watchPublic ? "Public — anyone with the link can watch" : "Private — link is disabled"}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Turn off any time to make the film private.</div>
        </div>
        <Switch data-testid="watch-public-toggle" checked={watchPublic} disabled={busy} onCheckedChange={onToggle} />
      </div>

      {watchPublic && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <code className="flex-1 min-w-0 truncate rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm font-mono" data-testid="watch-link-text">{watchUrl}</code>
          <button data-testid="copy-watch-link-button" onClick={() => { navigator.clipboard.writeText(watchUrl); toast.success("Watch link copied"); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]"><Copy size={15} /> Copy</button>
          <a data-testid="open-watch-page-link" href={watchUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold" style={{ background: `hsl(${theme.accent})` }}><ExternalLink size={15} /> Open</a>
        </div>
      )}
    </div>
  );
}

function RawDownloads({ clips, theme }) {
  const withFile = clips.filter((c) => c.file_id);
  if (withFile.length === 0) return null;
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
      <h4 className="font-semibold text-sm">Individual tributes</h4>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Download each raw clip.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {withFile.map((c) => (
          <a key={c.submission_id} data-testid={`raw-download-${c.submission_id}`} href={fileUrl(c.file_id)} download className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] p-2.5 hover:bg-[hsl(var(--muted))] text-sm">
            <Download size={15} style={{ color: `hsl(${theme.accent})` }} />
            <span className="flex-1 truncate">{c.contributor_name} <span className="text-[hsl(var(--muted-foreground))]">· {c.kind}</span></span>
          </a>
        ))}
      </div>
    </div>
  );
}
